import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload, IconInbox } from '../components/icons';
import { useConfig } from '../context/ConfigContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

// TS port of the source (Falah) app's pages/ProcurementOrderDetail.jsx.
function statusBadgeClass(status: string): string {
  if (['fully_received', 'matched_to_po', 'approved_for_payment', 'paid', 'completed'].includes(status)) {
    return 'badge-normal';
  }
  if (['rejected', 'discrepancy_found', 'issue_reported'].includes(status)) return 'badge-important';
  return 'badge-neutral';
}

function humanize(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface GoodsReceiptRow {
  id: number;
}

interface VendorInvoiceRow {
  id: number;
  invoice_number: string;
  invoice_amount: number;
  due_date: string | null;
  status: string;
}

interface PurchaseOrderRecord {
  id: number;
  po_number: string;
  vendor_name: string;
  amount: number;
  status: string;
  delivery_date: string | null;
  payment_terms: string | null;
  purchase_request: { id: number; reference: string } | null;
  goods_receipts: GoodsReceiptRow[];
  vendor_invoices: VendorInvoiceRow[];
}

export function ProcurementOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { settings } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [po, setPo] = useState<PurchaseOrderRecord | null>(null);

  useEffect(() => {
    api.get<PurchaseOrderRecord>(`/purchase-orders/${id}`).then((res) => setPo(res.data));
  }, [id]);

  if (!po) return null;

  const downloadPo = () => downloadFile(api, `/purchase-orders/${id}/pdf`, `${po.po_number}.pdf`);

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/procurement/orders')} />
          <IconButton icon={IconDownload} label="Download PO PDF" onClick={downloadPo} />
          <IconButton
            icon={IconInbox}
            label="Record it from Receiving"
            to="/procurement/receiving/new"
            hidden={po.goods_receipts && po.goods_receipts.length > 0}
          />
          {(!po.vendor_invoices || po.vendor_invoices.length === 0) && (
            <Link className="btn btn-primary" to="/procurement/payments/new">
              + Add Vendor Invoice
            </Link>
          )}
        </div>
      </header>

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{po.po_number}</div>
            <span className={`badge ${statusBadgeClass(po.status)}`}>{humanize(po.status)}</span>
            <div className="detail-summary-amount">{fmtMoney(po.amount)}</div>
            <div className="detail-summary-service">{po.vendor_name}</div>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Request</span>
            {po.purchase_request ? (
              <Link className="detail-item-value" to={`/procurement/requests/${po.purchase_request.id}`}>
                {po.purchase_request.reference}
              </Link>
            ) : (
              <span className="detail-item-value empty">Not available</span>
            )}
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Vendor</span>
            <span className="detail-item-value">{po.vendor_name}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Delivery date</span>
            <span className={`detail-item-value${po.delivery_date ? '' : ' empty'}`}>
              {po.delivery_date || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Payment terms</span>
            <span className={`detail-item-value${po.payment_terms ? '' : ' empty'}`}>
              {po.payment_terms || 'Not set'}
            </span>
          </div>
        </div>
      </div>

      {po.goods_receipts?.length > 0 && (
        <div className="card">
          <h2>Vendor Invoice</h2>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th className="right">Amount</th>
                  <th>Due</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {po.vendor_invoices?.map((inv) => (
                  <tr key={inv.id}>
                    <td className="muted">{inv.invoice_number}</td>
                    <td className="right">{fmtMoney(inv.invoice_amount)}</td>
                    <td>{inv.due_date ?? '—'}</td>
                    <td>
                      <span className={`badge ${statusBadgeClass(inv.status)}`}>{humanize(inv.status)}</span>
                    </td>
                    <td className="right">
                      <Link className="btn btn-ghost" to={`/procurement/payments/${inv.id}`}>
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
                {(!po.vendor_invoices || po.vendor_invoices.length === 0) && (
                  <tr>
                    <td colSpan={5} className="muted center">
                      No vendor invoices recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
